import type {
  DesignPartnerConfig,
  DesignPartnerMessage,
  GameMode,
  GameParticipant,
  GameQuestion,
  GameSession,
} from '@/types/models';
import { api } from '@/lib/firebase/api';
import { ensureDeviceId } from '@/lib/deviceId';

interface CreateSessionPayload {
  courseId: string;
  title: string;
  questions: GameQuestion[];
  mode?: GameMode;
  designPartner?: DesignPartnerConfig;
}

interface PatchSessionPayload {
  status?: 'waiting' | 'active' | 'ended';
  currentQuestionIndex?: number;
  displayQuestionIndex?: number;
  revealLeaderboard?: boolean;
}

type AnswerPayload =
  | { selectedIndex: number }
  | { matched: Record<string, number> }
  | { selectedWords: string[] }
  | { text: string }
  | { appName: string; handle: string; text: string; icon: string; color: string };

export const createSession = (body: CreateSessionPayload) =>
  api<GameSession>('/sessions', { method: 'POST', body: JSON.stringify(body) });

export const getSessionByCode = (code: string) =>
  api<GameSession>(`/sessions/code/${code}`);

export const patchSession = (id: string, body: PatchSessionPayload) =>
  api<GameSession>(`/sessions/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const joinSession = (id: string, name: string) =>
  api<GameParticipant>(`/sessions/${id}/participants`, {
    method: 'POST',
    body: JSON.stringify({ name, deviceId: ensureDeviceId() }),
  });

export const submitAnswer = (id: string, pid: string, questionIndex: number, answer: AnswerPayload) =>
  api<GameParticipant>(`/sessions/${id}/participants/${pid}/answers`, {
    method: 'POST',
    body: JSON.stringify({ questionIndex, ...answer }),
  });

export const confirmQuestion = (id: string, pid: string, questionIndex: number) =>
  api<GameParticipant>(`/sessions/${id}/participants/${pid}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ questionIndex }),
  });

export const removeParticipant = (id: string, pid: string) =>
  api<null>(`/sessions/${id}/participants/${pid}`, { method: 'DELETE' });

export const getLeaderboard = (id: string) =>
  api<GameParticipant[]>(`/sessions/${id}/leaderboard`);

export const toggleTweetLike = (sessionId: string, questionIndex: number, authorPid: string, likerPid: string) =>
  api<{ likers: string[] }>(`/sessions/${sessionId}/tweets/${questionIndex}/${authorPid}/likes`, {
    method: 'POST',
    body: JSON.stringify({ likerPid }),
  });

// ── Design Partner (Flo) ─────────────────────────────────────────────────────

interface DesignPartnerAnswersPayload {
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  challenge?: string;
}

export const submitDesignPartnerAnswers = (
  sessionId: string,
  pid: string,
  body: DesignPartnerAnswersPayload,
) =>
  api<GameParticipant>(`/sessions/${sessionId}/participants/${pid}/dp/answers`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const startDesignPartnerConversation = (sessionId: string, pid: string) =>
  api<{ participant: GameParticipant; reply: DesignPartnerMessage }>(
    `/sessions/${sessionId}/participants/${pid}/dp/start`,
    { method: 'POST' },
  );

export const sendDesignPartnerMessage = (sessionId: string, pid: string, content: string) =>
  api<{ participant: GameParticipant; reply: DesignPartnerMessage }>(
    `/sessions/${sessionId}/participants/${pid}/dp/messages`,
    { method: 'POST', body: JSON.stringify({ content }) },
  );

export const recordDesignPartnerFeedback = (
  sessionId: string,
  pid: string,
  msgIndex: number,
  feedback: 'up' | 'down',
) =>
  api<null>(
    `/sessions/${sessionId}/participants/${pid}/dp/messages/${msgIndex}/feedback`,
    { method: 'PATCH', body: JSON.stringify({ feedback }) },
  );

export const regenerateDesignPartnerMessage = (sessionId: string, pid: string) =>
  api<{ participant: GameParticipant; reply: DesignPartnerMessage }>(
    `/sessions/${sessionId}/participants/${pid}/dp/regenerate`,
    { method: 'POST' },
  );

export const resetDesignPartnerConversation = (sessionId: string, pid: string) =>
  api<null>(`/sessions/${sessionId}/participants/${pid}/dp/reset`, { method: 'POST' });

export const endDesignPartnerSession = (sessionId: string, pid: string) =>
  api<GameParticipant>(`/sessions/${sessionId}/participants/${pid}/dp/end`, { method: 'POST' });
